import { Box, Chip } from '@mui/material';
import InfiniteScroll from 'components/common/InfiniteScroll';
import React, {
  createRef,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { OPEN_CHANNEL } from 'store/app/ui/ChannelsPanel/action';
import {
  useListMessagesQuery,
  useLazyListMessagesQuery,
  useReadMessagesMutation,
} from 'store/app/entities/MessagesApiSlice';
import { MessagesLoadingContext } from 'components/@home/@messages/ChatPanel/ChatPanelContext';
import authUser from 'store/selectors/authUser';
import selectedMessageId from 'store/selectors/selectedMessageId';
import dateDaily from 'utils/dateDaily';
import groupAndSortMessages from 'utils/groupAndSortMessages';
import { I18n } from 'utils/i18n';
import MessageGroup from './MessageGroup';

const MessagesList = (_, ref) => {
  // The URL param is the authoritative channel identity (same source as
  // MessagesWrapper's query). The `currentChannel` selector is unreliable here:
  // when the open channel is not in the channels map it falls back to a
  // *different* channel, which strands pagination and reads the wrong cache (OMM-603).
  const { channelId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const searchAroundId = location.state?.messageId || null;
  const channel = useSelector(state => state.channels.channels[channelId]);
  const selectedMsgId = useSelector(selectedMessageId);
  const listAroundId = searchAroundId || selectedMsgId || null;
  const hasFocus = useSelector(state => state.elements.Focus.hasFocus);
  const user = useSelector(authUser);
  const employees = useSelector(state => state.employees.employees);
  const channelUsers = useSelector(state => state.channels.channels[channelId]?.users);
  const users = useSelector(state => state.users);

  // `currentData` (not `data`): RTK Query keeps `data` from the previously selected
  // channel during an arg transition, which would let `messages`/the pagination anchor
  // belong to a different channel than `channelId` and fire a cross-channel `beforeId`
  // request that corrupts the cache (OMM-603).
  const { currentData: data } = useListMessagesQuery(
    { channelId, ...(listAroundId ? { aroundId: listAroundId } : {}) },
    { skip: !channelId },
  );
  const [triggerLoadMore] = useLazyListMessagesQuery();
  const [readMessagesMutation] = useReadMessagesMutation();

  const messages = useMemo(() => {
    if (!data?.messages || !user) return [];
    const enriched = data.messages.filter(Boolean).map(m => {
      const employeeBase =
        (channelUsers ? channelUsers.find(u => u._id === m.employee_id) : null) ||
        employees[m.employee_id] ||
        m.from;
      const employee =
        employeeBase && m.from?.avatar
          ? { ...employeeBase, avatar: m.from.avatar }
          : employeeBase || m.from;

      return {
        ...m,
        isMine: (m.user_id || m.from?._id) === user._id,
        replyTo: m.replyTo ? { ...m.replyTo, isMine: m.replyTo.from === user._id } : null,
        dateTime: dateDaily(m.createdAt),
        employee,
        user: users[m.user_id],
        seen: m.isRead,
      };
    });
    return groupAndSortMessages(enriched);
  }, [data?.messages, employees, channelUsers, users, user]);

  const [highlightedMessageId, setHighlightedMessageId] = useState(selectedMsgId);
  const messageList = useRef();
  const channelIdRef = useRef(channelId);
  channelIdRef.current = channelId;
  // When true, content growth (streaming text, new messages) keeps the view pinned to the bottom.
  const stickToBottomRef = useRef(true);
  // Snapshot taken before loading older messages; useLayoutEffect applies correction after DOM commit
  const scrollRestoreRef = useRef(null);
  // Channel id we have already positioned (initial scroll to bottom / selected message).
  // Render-timing independent: channelId (URL) is known before messages load, so a
  // `!prevChannelId` check fires too early and leaves the view stuck at the top.
  const initialScrollChannelRef = useRef(null);
  // End-of-history is driven by the merge flags on the RTK cache: when a
  // beforeId/fromId page comes back with fewer than MESSAGES_PER_PAGE items
  // there is no more history in that direction (OMM-603).
  const hasBelow = data?.hasOlder ?? true;
  const hasAbove = data?.hasNewer ?? false;
  // Block loadMore only during aroundId fetch / search anchor — not hasAbove/hasBelow
  // (forcing both false detaches InfiniteScroll listeners and breaks scroll).
  const searchAnchorActiveRef = useRef(false);
  const anchorStabilizeCleanupRef = useRef(null);
  const isBlockingPagination = Boolean(data?.awaitingAroundId || selectedMsgId);
  const isSearchAnchoring = Boolean(selectedMsgId || data?.awaitingAroundId || listAroundId);
  if (isSearchAnchoring) {
    searchAnchorActiveRef.current = true;
    stickToBottomRef.current = false;
  }

  const cancelAnchorStabilize = useCallback(() => {
    anchorStabilizeCleanupRef.current?.();
    anchorStabilizeCleanupRef.current = null;
  }, []);

  // Group messages by day so each date chip lives inside its own section and
  // CSS `position: sticky` makes it kick the previous chip out as you scroll.
  const daySections = useMemo(() => {
    const sections = [];
    let current = null;
    messages.forEach(message => {
      if (message.dateTime || !current) {
        current = { dateTime: message.dateTime || null, messages: [] };
        sections.push(current);
      }
      current.messages.push(message);
    });
    return sections;
  }, [messages]);

  // InfiniteScroll captures `_scrollListener` once (when `scrollComponent` is
  // first set) and never re-attaches when props change. On a cold load that
  // closure snapshots `firstLoadedId === undefined` (data hasn't resolved
  // yet), so the first scroll-to-top would call `loadMore(undefined, …)` and
  // refetch the first page with no anchor — wiping the merged cache without
  // adding any history. We keep refs in sync (during render) with the latest
  // non-temp ends so `loadMore` always uses the freshest anchor.
  const oldestNonTempIdRef = useRef(null);
  const newestNonTempIdRef = useRef(null);
  oldestNonTempIdRef.current = null;
  newestNonTempIdRef.current = null;
  for (let i = 0; i < messages.length; i += 1) {
    if (!messages[i].temp) {
      oldestNonTempIdRef.current = messages[i]._id;
      break;
    }
  }
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (!messages[i].temp) {
      newestNonTempIdRef.current = messages[i]._id;
      break;
    }
  }
  // InfiniteScroll debounces loadMore once per listener attach (hasAbove/hasBelow
  // changes during search anchor). Read live pagination state from a ref so a
  // closure captured while isBlockingPagination was true cannot block forever.
  const loadMoreCtxRef = useRef({});
  loadMoreCtxRef.current = {
    isBlockingPagination,
    messages,
    awaitingAroundId: Boolean(data?.awaitingAroundId),
    selectedMsgId,
  };
  const [messagesRef, setMessagesRefs] = useState(
    messages.reduce(
      (ret, message) => ({
        ...ret,
        // Dynamic ref map: useRef cannot be called per message.
        [`message-${message._id}`]: createRef(), // eslint-disable-line @eslint-react/no-create-ref
      }),
      {},
    ),
  );
  const messagesRefRef = useRef(messagesRef);
  messagesRefRef.current = messagesRef;

  useEffect(() => {
    setMessagesRefs(
      messages.reduce(
        (ret, message) => ({
          ...ret,
          // eslint-disable-next-line @eslint-react/no-create-ref -- dynamic ref map; useRef cannot be called per message
          [`message-${message._id}`]: messagesRef[`message-${message._id}`] || createRef(),
        }),
        {},
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- preserve existing ref objects when rebuilding message refs map
  }, [messages]);

  const { loadingMoreMessages, setLoadingMoreMessages } = useContext(MessagesLoadingContext);

  const firstUnread = useMemo(() => {
    const { lastReadMessage, messagesUnreadCount } = channel || {};
    if (!lastReadMessage || messagesUnreadCount <= 0) return null;
    return messages.find(m => !m.isMine && m._id > lastReadMessage) || null;
  }, [messages, channel]);

  const [showUnreadLine, setShowUnreadLine] = useState(false);
  useEffect(() => {
    if (firstUnread) setShowUnreadLine(true);
  }, [firstUnread]);
  useEffect(() => {
    setShowUnreadLine(false);
    scrollRestoreRef.current = null;
    setLoadingMoreMessages(null);
  }, [channelId, setLoadingMoreMessages]);

  const usePrevious = value => {
    const previousRef = useRef();
    useEffect(() => {
      previousRef.current = value;
    });
    return previousRef.current;
  };
  const prevChannel = usePrevious(channel);
  const prevChannelId = usePrevious(channelId);
  const prevHasFocus = usePrevious(hasFocus);
  const prevLastMessageId = prevChannel?.lastMessage_id;
  const lastMessageId = channel?.lastMessage_id;
  const prevMessages = usePrevious(messages);
  const hasDifferentMessagesLength = (prevMessages?.length ?? 0) !== messages.length;
  const tailScrollKey = useMemo(() => {
    if (!messages.length) return '';
    const last = messages[messages.length - 1];
    return `${last._id}:${last.text?.length ?? 0}:${last.attachments?.length ?? 0}`;
  }, [messages]);

  useEffect(() => {
    if (selectedMsgId) {
      searchAnchorActiveRef.current = true;
      setHighlightedMessageId(current => (current === selectedMsgId ? current : null));
    }
  }, [selectedMsgId]);

  useEffect(() => {
    cancelAnchorStabilize();
    searchAnchorActiveRef.current = false;
    stickToBottomRef.current = true;
  }, [channelId, cancelAnchorStabilize]);

  useEffect(() => () => cancelAnchorStabilize(), [cancelAnchorStabilize]);

  const finishSearchAnchor = useCallback(() => {
    searchAnchorActiveRef.current = false;
    stickToBottomRef.current = false;
    dispatch({
      type: OPEN_CHANNEL,
      payload: channelIdRef.current,
      meta: { messageId: null },
    });
    if (location.state?.messageId) {
      const restState = { ...location.state };
      delete restState.messageId;
      navigate(location.pathname, { replace: true, state: restState });
    }
  }, [dispatch, location.pathname, location.state, navigate]);

  const scrollToMessage = (messageId, trial = 0) => {
    const refKey = `message-${messageId}`;
    const node = messagesRefRef.current[refKey]?.current;
    if (!node && trial < 20) {
      setTimeout(() => scrollToMessage(messageId, trial + 1), 100);
      return;
    }
    if (!node) {
      cancelAnchorStabilize();
      finishSearchAnchor();
      return;
    }
    stickToBottomRef.current = false;
    const scrollDom = () => {
      const anchorNode = messagesRefRef.current[refKey]?.current;
      if (!anchorNode) return;
      anchorNode?.scrollIntoView?.({ block: 'center' });
    };
    scrollDom();
    setHighlightedMessageId(messageId);

    // Images/video above the anchor often finish loading after the first scrollIntoView.
    // Re-center while scrollHeight changes, then release the anchor session.
    cancelAnchorStabilize();
    const element = messageList.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      requestAnimationFrame(() => {
        requestAnimationFrame(finishSearchAnchor);
      });
      return;
    }

    const stabilize = {
      lastHeight: element.scrollHeight,
      timers: { settle: null, max: null },
      end() {
        this.observer.disconnect();
        clearTimeout(this.timers.settle);
        clearTimeout(this.timers.max);
        if (anchorStabilizeCleanupRef.current === stabilize.end) {
          anchorStabilizeCleanupRef.current = null;
        }
        finishSearchAnchor();
      },
      scheduleFinish() {
        clearTimeout(this.timers.settle);
        this.timers.settle = setTimeout(() => this.end(), 400);
      },
    };
    stabilize.observer = new ResizeObserver(() => {
      if (element.scrollHeight === stabilize.lastHeight) return;
      stabilize.lastHeight = element.scrollHeight;
      scrollDom();
      stabilize.scheduleFinish();
    });
    anchorStabilizeCleanupRef.current = stabilize.end;
    stabilize.observer.observe(element);
    stabilize.timers.max = setTimeout(() => stabilize.end(), 3000);
    stabilize.scheduleFinish();
  };

  const scrollToSelectedMessage = (force = false) => {
    const element = messageList.current;
    if (element && (force || !prevChannelId)) {
      if (selectedMsgId) {
        scrollToMessage(selectedMsgId);
      } else {
        element.scrollTop = element.scrollHeight;
        stickToBottomRef.current = true;
      }
    }
  };

  const isNearBottom = useCallback(() => {
    const element = messageList.current;
    if (!element) return true;
    return element.scrollHeight - element.scrollTop - element.clientHeight < 200;
  }, []);

  const updateStickToBottom = useCallback(() => {
    stickToBottomRef.current = isNearBottom();
  }, [isNearBottom]);

  useEffect(() => {
    const element = messageList.current;
    if (!element) return undefined;
    element.addEventListener('scroll', updateStickToBottom, { passive: true });
    return () => element.removeEventListener('scroll', updateStickToBottom);
  }, [channelId, updateStickToBottom]);

  /** @param {string} [anchorMessageId] — when set, scroll that row into view and re-sync max scroll (voice bubbles layout after paint; composer height changes when recording UI unmounts). */
  const scrollToBottom = useCallback(
    anchorMessageId => {
      if (searchAnchorActiveRef.current || selectedMsgId) return;
      const scrollToMax = () => {
        const element = messageList.current;
        if (element) {
          element.scrollTop = element.scrollHeight;
        }
      };

      const scrollAnchorEnd = (messageId, trial = 0) => {
        const refs = messagesRefRef.current;
        const refKey = `message-${messageId}`;
        const node = refs[refKey]?.current;
        if (refs[refKey] && !node && trial < 20) {
          setTimeout(() => scrollAnchorEnd(messageId, trial + 1), 50);
          return;
        }
        if (node) {
          const dom = node?.nodeType === 1 ? node : null;
          if (dom?.scrollIntoView) {
            dom.scrollIntoView({ block: 'end', inline: 'nearest', behavior: 'auto' });
          }
        }
        scrollToMax();
      };

      if (anchorMessageId) {
        requestAnimationFrame(() => {
          scrollAnchorEnd(anchorMessageId);
          requestAnimationFrame(scrollToMax);
        });
        setTimeout(() => {
          scrollAnchorEnd(anchorMessageId);
          scrollToMax();
        }, 100);
        setTimeout(scrollToMax, 320);
      } else {
        requestAnimationFrame(() => {
          scrollToMax();
          requestAnimationFrame(scrollToMax);
        });
      }
    },
    [selectedMsgId],
  );

  useImperativeHandle(ref, () => ({
    scrollToSelectedMessage,
    scrollToBottom,
  }));

  // Run before tail-pin and InfiniteScroll react to the aroundId page landing.
  useLayoutEffect(() => {
    if (!selectedMsgId || highlightedMessageId === selectedMsgId) return;
    if (!messages.some(m => m._id === selectedMsgId)) return;
    stickToBottomRef.current = false;
    scrollToMessage(selectedMsgId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scrollToMessage uses refs; retries until DOM is ready
  }, [messages, selectedMsgId, highlightedMessageId]);

  useEffect(() => {
    if (!loadingMoreMessages) {
      // First time we have messages for this channel (initial open or channel
      // switch): position at the bottom (or the selected/deep-linked message).
      const needsInitialScroll =
        messages.length > 0 && initialScrollChannelRef.current !== channelId;
      if (needsInitialScroll) {
        initialScrollChannelRef.current = channelId;
        scrollToSelectedMessage(true);
      } else if (
        !isSearchAnchoring &&
        lastMessageId &&
        lastMessageId !== prevLastMessageId &&
        isNearBottom()
      ) {
        stickToBottomRef.current = true;
        scrollToBottom();
      }
      if (channel?.messagesUnreadCount > 0 && !prevHasFocus && hasFocus) {
        readMessagesMutation({ channelId });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scroll positioning uses refs and selective deps to avoid scroll loops
  }, [
    hasFocus,
    loadingMoreMessages,
    lastMessageId,
    prevLastMessageId,
    channelId,
    hasDifferentMessagesLength,
    isSearchAnchoring,
  ]);

  // Agent messages stream word-by-word (`messageStream` socket): text grows without
  // changing message count or channel.lastMessage_id, so keep pinned views following.
  useLayoutEffect(() => {
    if (loadingMoreMessages || !stickToBottomRef.current || !messages.length) return;
    if (isSearchAnchoring || searchAnchorActiveRef.current) return;
    scrollToBottom(messages[messages.length - 1]?._id);
  }, [tailScrollKey, loadingMoreMessages, messages, scrollToBottom, isSearchAnchoring]);

  useLayoutEffect(() => {
    if (!scrollRestoreRef.current || !messageList.current) return;
    const {
      channelId: snapshotChannelId,
      scrollTop,
      scrollHeight: oldScrollHeight,
    } = scrollRestoreRef.current;
    if (snapshotChannelId !== channelId) {
      scrollRestoreRef.current = null;
      return;
    }
    messageList.current.scrollTop =
      scrollTop + (messageList.current.scrollHeight - oldScrollHeight);
    scrollRestoreRef.current = null;
  }, [messages.length, channelId]);

  const loadMore = useCallback(
    (_messageId, loading) => {
      const { isBlockingPagination: blocking, messages: liveMessages } = loadMoreCtxRef.current;
      // InfiniteScroll calls setLoading then loadMore. If we abort, reset parent loading
      // so InfiniteScroll's isLoadingProp sync re-attaches the scroll listener.
      const releaseInfiniteScrollLoading = () => {
        setLoadingMoreMessages(loading);
        requestAnimationFrame(() => setLoadingMoreMessages(null));
      };
      // When both edges are within threshold (e.g. centered search anchor), pick one direction.
      let loadBelow = Boolean(loading?.loadBelow);
      let loadAbove = Boolean(loading?.loadAbove);
      if (loadBelow && loadAbove) {
        const scrollEl = messageList.current;
        if (scrollEl) {
          const distTop = scrollEl.scrollTop;
          const distBottom = scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;
          if (distTop <= distBottom) {
            loadAbove = false;
          } else {
            loadBelow = false;
          }
        }
      }
      const resolvedLoading = { loadBelow, loadAbove };
      if (blocking) {
        releaseInfiniteScrollLoading();
        return;
      }
      if (!resolvedLoading.loadBelow && !resolvedLoading.loadAbove) {
        releaseInfiniteScrollLoading();
        return;
      }
      // Read the anchor from refs, not from the messageId argument: InfiniteScroll
      // closes over its props once and can hand us a stale (undefined) value.
      const nonTempMessages = liveMessages.filter(m => !m.temp);
      const anchorId = resolvedLoading.loadBelow
        ? oldestNonTempIdRef.current
        : newestNonTempIdRef.current;
      if (!anchorId) {
        // No anchor yet — abort instead of refetching the first page with no
        // bounds, which would replace the merged cache and break pagination.
        releaseInfiniteScrollLoading();
        return;
      }
      if (!nonTempMessages.some(m => m._id === anchorId)) {
        releaseInfiniteScrollLoading();
        return;
      }
      const expectedAnchor = resolvedLoading.loadBelow
        ? nonTempMessages[0]?._id
        : nonTempMessages[nonTempMessages.length - 1]?._id;
      if (anchorId !== expectedAnchor) {
        releaseInfiniteScrollLoading();
        return;
      }

      const element = messageList.current;
      const channelIdAtLoad = channelIdRef.current;
      setLoadingMoreMessages(resolvedLoading);

      const options = {
        channelId: channelIdAtLoad,
        [resolvedLoading.loadBelow ? 'beforeId' : 'fromId']: anchorId,
      };

      // Snapshot scroll position before loading older messages so useLayoutEffect can restore it
      if (resolvedLoading.loadBelow) {
        scrollRestoreRef.current = {
          channelId: channelIdAtLoad,
          scrollTop: element.scrollTop,
          scrollHeight: element.scrollHeight,
        };
      }

      triggerLoadMore(options).then(({ data: loaded }) => {
        if (channelIdAtLoad !== channelIdRef.current) return;
        setLoadingMoreMessages(null);
        if (resolvedLoading.loadBelow) {
          // data.messages is desc (newest first) — last element is the oldest loaded
          const oldest = loaded?.messages?.[loaded.messages.length - 1]?._id;
          readMessagesMutation({ channelId: channelIdAtLoad, firstReadMessage: oldest });
        }
      });
    },
    [readMessagesMutation, setLoadingMoreMessages, triggerLoadMore],
  );

  return (
    <Box
      ref={messageList}
      data-testid="messages-panel-scroll"
      sx={{
        flexGrow: 1,
        padding: '15px 20px',
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        overflowX: 'hidden',
        paddingBottom: '30px',
      }}
    >
      <InfiniteScroll
        isReverse
        isLoading={loadingMoreMessages}
        initialLoad={false}
        firstLoadedId={messages.find(m => !m.temp)?._id}
        lastLoadedId={
          messages
            .slice()
            .reverse()
            .find(m => !m.temp)?._id
        }
        threshold={600}
        loadMore={loadMore}
        hasBelow={hasBelow}
        hasAbove={hasAbove}
      >
        {daySections.map((section, idx) => (
          <Box
            // dateTime can repeat (no messages on a day is impossible here, but be safe);
            // first non-dated section can only ever be the leading one.
            key={section.dateTime || `__nodate-${idx}`}
            sx={{ display: 'flex', flexDirection: 'column' }}
          >
            {section.dateTime && (
              <Box
                data-testid="messages-date-chip"
                sx={{
                  position: 'sticky',
                  top: '-12px',
                  zIndex: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  margin: '12px 0 4px',
                }}
              >
                <Chip
                  label={
                    idx === 0 && data?.hasOlder === false
                      ? `${I18n.t('MessagesList.Start')} · ${section.dateTime}`
                      : section.dateTime
                  }
                  size="small"
                  variant="outlined"
                  sx={theme => ({
                    pointerEvents: 'auto',
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
                    borderColor: theme.palette.divider,
                    '& .MuiChip-label': {
                      fontWeight: 500,
                    },
                  })}
                />
              </Box>
            )}
            {section.messages.map(message => {
              const line = showUnreadLine && firstUnread?._id === message._id;
              return (
                <MessageGroup
                  msgRef={messagesRef[`message-${message._id}`]}
                  key={`${message._id}-${line}`}
                  message={message}
                  line={line}
                  isSelected={message._id === highlightedMessageId}
                  scrollToMessage={scrollToMessage}
                  hideDate
                />
              );
            })}
          </Box>
        ))}
        <div style={{ height: 25 }} />
      </InfiniteScroll>
    </Box>
  );
};

export default forwardRef(MessagesList);
